/**
 * Lightweight, robust Markdown to HTML parser tailored for technical documentation.
 * Supports:
 * - Headings (h1-h6) with slugified IDs
 * - GitHub-style Alert/Callout blocks ([!NOTE], [!TIP], [!IMPORTANT], [!WARNING], [!CAUTION])
 * - Fenced code blocks with language tags and copy button support
 * - Markdown tables with aligned headers and cells
 * - Blockquotes
 * - Unordered and ordered lists (including task lists / checkboxes)
 * - Horizontal rules
 * - Inline formatting (bold, italic, strikethrough, inline code, links, images)
 */

function escapeHtml(text) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function slugify(text) {
  return text
    .toLowerCase()
    .replace(/<[^>]+>/g, '')
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function renderMarkdown(markdown) {
  if (!markdown) return '';

  // Normalize CRLF/CR -> LF before splitting. A stray trailing \r survives
  // split('\n') and breaks any regex anchored with `.` + `$` (JS's `.`
  // excludes \r, so `(.+)$` can't reach the real end of the line) --
  // headingMatch below silently fails on every heading in a CRLF file,
  // while the paragraph loop's own `!line.startsWith('#')` guard still
  // rejects that same line (startsWith isn't affected by a trailing \r).
  // Neither branch consumes the line, i never advances, and the whole
  // parser hangs the tab forever on the first heading it meets -- exactly
  // what happened on ORDER_SYSTEM.md (a CRLF file, /docs's default page).
  const lines = markdown.replace(/\r\n?/g, '\n').split('\n');
  const html = [];
  let i = 0;
  const slugCounts = {};

  const getSlug = (text) => {
    let slug = slugify(text) || 'section';
    if (slugCounts[slug] !== undefined) {
      slugCounts[slug]++;
      slug = `${slug}-${slugCounts[slug]}`;
    } else {
      slugCounts[slug] = 0;
    }
    return slug;
  };

  while (i < lines.length) {
    const line = lines[i];

    // 1. Fenced Code Blocks (``` or ````)
    const codeMatch = line.match(/^(`{3,4})\s*([\w-]*)/);
    if (codeMatch) {
      const delimiter = codeMatch[1];
      const lang = codeMatch[2] || 'text';
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].startsWith(delimiter)) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // skip closing delimiter
      const rawCode = codeLines.join('\n');
      const escapedCode = escapeHtml(rawCode);
      const isMermaid = lang.toLowerCase() === 'mermaid';

      html.push(`
        <div class="doc-code-block my-3 ${isMermaid ? 'doc-mermaid-block' : ''}">
          <div class="doc-code-header d-flex justify-content-between align-items-center px-3 py-1 bg-dark text-white-50 border-bottom border-secondary rounded-top">
            <span class="small text-uppercase fw-bold"><i class="fa fa-code me-1"></i>${escapeHtml(lang)}</span>
            <button class="btn btn-sm text-white-50 p-0 doc-copy-btn" data-copy-code="${encodeURIComponent(rawCode)}">
              <i class="fa fa-copy me-1"></i>Copy
            </button>
          </div>
          <pre class="bg-dark text-light p-3 m-0 rounded-bottom overflow-auto" style="font-size: 0.875rem; font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;"><code>${escapedCode}</code></pre>
        </div>
      `);
      continue;
    }

    // 2. Blockquotes & GitHub-style Callouts (> [!NOTE], > [!WARNING], etc.)
    if (line.startsWith('>')) {
      const quoteLines = [];
      while (i < lines.length && lines[i].startsWith('>')) {
        quoteLines.push(lines[i].replace(/^>\s?/, ''));
        i++;
      }

      const firstLine = quoteLines[0] || '';
      const alertMatch = firstLine.match(/^\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]/i);

      if (alertMatch) {
        const alertType = alertMatch[1].toUpperCase();
        quoteLines.shift(); // remove marker line
        const alertContent = quoteLines.map((l) => formatInline(escapeHtml(l))).join('<br/>');

        const alertConfig = {
          NOTE: { color: 'info', icon: 'fa-circle-info', title: 'Note' },
          TIP: { color: 'success', icon: 'fa-lightbulb', title: 'Tip' },
          IMPORTANT: { color: 'primary', icon: 'fa-circle-exclamation', title: 'Important' },
          WARNING: { color: 'warning', icon: 'fa-triangle-exclamation', title: 'Warning' },
          CAUTION: { color: 'danger', icon: 'fa-radiation', title: 'Caution' },
        }[alertType] || { color: 'secondary', icon: 'fa-circle-info', title: alertType };

        html.push(`
          <div class="alert alert-${alertConfig.color} border-start border-4 border-${alertConfig.color} my-3 shadow-sm py-2 px-3">
            <div class="d-flex align-items-center mb-1">
              <i class="fa ${alertConfig.icon} text-${alertConfig.color} me-2"></i>
              <strong class="text-${alertConfig.color}">${alertConfig.title}</strong>
            </div>
            <div class="small ps-4">${alertContent}</div>
          </div>
        `);
      } else {
        const quoteContent = quoteLines.map((l) => formatInline(escapeHtml(l))).join('<br/>');
        html.push(`
          <blockquote class="blockquote border-start border-4 border-primary ps-3 my-3 text-muted" style="font-size: 0.95rem;">
            ${quoteContent}
          </blockquote>
        `);
      }
      continue;
    }

    // 3. Tables (| Col 1 | Col 2 |)
    if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
        tableLines.push(lines[i].trim());
        i++;
      }

      if (tableLines.length >= 2) {
        const headerRow = tableLines[0];
        const separatorRow = tableLines[1];
        const bodyRows = tableLines.slice(2);

        const parseCells = (row) =>
          row
            .slice(1, -1)
            .split('|')
            .map((c) => c.trim());

        const headers = parseCells(headerRow);
        const alignments = parseCells(separatorRow).map((cell) => {
          if (cell.startsWith(':') && cell.endsWith(':')) return 'center';
          if (cell.endsWith(':')) return 'end';
          return 'start';
        });

        const headerHtml = headers
          .map((h, idx) => `<th class="text-${alignments[idx] || 'start'} py-2 px-3">${formatInline(escapeHtml(h))}</th>`)
          .join('');

        const bodyHtml = bodyRows
          .map((row) => {
            const cells = parseCells(row);
            const rowHtml = cells
              .map((c, idx) => `<td class="text-${alignments[idx] || 'start'} py-2 px-3">${formatInline(escapeHtml(c))}</td>`)
              .join('');
            return `<tr>${rowHtml}</tr>`;
          })
          .join('');

        html.push(`
          <div class="table-responsive my-3 shadow-sm rounded border">
            <table class="table table-hover table-striped mb-0" style="font-size: 0.875rem;">
              <thead class="table-light border-bottom">
                <tr>${headerHtml}</tr>
              </thead>
              <tbody>
                ${bodyHtml}
              </tbody>
            </table>
          </div>
        `);
        continue;
      }
    }

    // 4. Headings (# H1 to ###### H6)
    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const rawText = headingMatch[2].trim();
      const slug = getSlug(rawText);
      const formattedText = formatInline(escapeHtml(rawText));

      const headingClasses = {
        1: 'border-bottom pb-2 mt-4 mb-3 text-primary fw-bold',
        2: 'border-bottom pb-1 mt-4 mb-2 text-dark fw-semibold',
        3: 'mt-3 mb-2 text-secondary fw-semibold',
        4: 'mt-3 mb-1 fw-bold',
        5: 'mt-2 mb-1 fw-bold',
        6: 'mt-2 mb-1 fw-bold text-muted',
      }[level] || '';

      html.push(`
        <h${level} id="${slug}" class="doc-heading doc-h${level} ${headingClasses}">
          <a href="#${slug}" class="text-decoration-none text-reset">${formattedText}</a>
          <a href="#${slug}" class="doc-anchor text-muted ms-2 opacity-0 text-decoration-none" title="Direct link"><i class="fa fa-link small"></i></a>
        </h${level}>
      `);
      i++;
      continue;
    }

    // 5. Horizontal Rules (---, ***, ___)
    if (/^(\*{3,}|-{3,}|_{3,})$/.test(line.trim())) {
      html.push('<hr class="my-4 text-secondary opacity-25" />');
      i++;
      continue;
    }

    // 6. Lists (Unordered & Ordered & Task Lists)
    const listMatch = line.match(/^(\s*)([-*+]|\d+\.)\s+(.+)$/);
    if (listMatch) {
      const isOrdered = /^\d+\./.test(listMatch[2]);
      const tag = isOrdered ? 'ol' : 'ul';
      const items = [];

      while (i < lines.length) {
        const itemMatch = lines[i].match(/^(\s*)([-*+]|\d+\.)\s+(.+)$/);
        if (!itemMatch) break;

        let itemText = itemMatch[3];
        // Task list checkbox support [ ] or [x]
        itemText = itemText.replace(/^\[ \]\s*/, '<input type="checkbox" disabled class="form-check-input me-2 m-0" />');
        itemText = itemText.replace(/^\[[xX]\]\s*/, '<input type="checkbox" checked disabled class="form-check-input me-2 m-0" />');

        items.push(`<li class="mb-1">${formatInline(escapeHtml(itemText))}</li>`);
        i++;
      }

      html.push(`<${tag} class="my-2 ps-4" style="font-size: 0.925rem;">${items.join('')}</${tag}>`);
      continue;
    }

    // 7. Empty line
    if (!line.trim()) {
      i++;
      continue;
    }

    // 8. Normal Paragraph
    const paragraphLines = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !lines[i].startsWith('#') &&
      !lines[i].startsWith('>') &&
      !lines[i].startsWith('```') &&
      !lines[i].startsWith('`') &&
      !lines[i].match(/^(\s*)([-*+]|\d+\.)\s+/) &&
      !(lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) &&
      !/^(\*{3,}|-{3,}|_{3,})$/.test(lines[i].trim())
    ) {
      paragraphLines.push(lines[i]);
      i++;
    }

    const pContent = paragraphLines.map((l) => formatInline(escapeHtml(l))).join(' ');
    if (pContent.trim()) {
      html.push(`<p class="my-2" style="font-size: 0.925rem; line-height: 1.65;">${pContent}</p>`);
    }

    // Defensive forward-progress guard: reaching here means every earlier
    // construct check (code fence, blockquote, table, heading, hr, list)
    // already failed to match this line, but the paragraph loop's own
    // (differently-phrased) guard can still reject that same line on some
    // future edge case neither set of conditions was written to agree on
    // -- exactly what happened with a stray \r. If that ever happens again,
    // paragraphLines stays empty and i never moves, hanging the tab in an
    // infinite loop instead of just mis-rendering one line. Never let a
    // parse bug become a hang: always consume at least this one line.
    if (paragraphLines.length === 0) {
      i++;
    }
  }

  return html.join('\n');
}

/**
 * Parses inline markdown: bold, italic, code, links, images, math badges.
 */
function formatInline(text) {
  return text
    // Inline code `code`
    .replace(/`([^`]+)`/g, '<code class="bg-light text-danger px-1 rounded border" style="font-size: 0.875em; font-family: monospace;">$1</code>')
    // Bold & Italic ***text***
    .replace(/\*\*\*([^*]+)\*\*\*/g, '<strong><em>$1</em></strong>')
    // Bold **text** or __text__
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/__([^_]+)__/g, '<strong>$1</strong>')
    // Italic *text* or _text_
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/_([^_]+)_/g, '<em>$1</em>')
    // Strikethrough ~~text~~
    .replace(/~~([^~]+)~~/g, '<del>$1</del>')
    // Images ![alt](url)
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" class="img-fluid rounded border my-2" />')
    // Markdown links [text](url)
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, linkText, url) => {
      const isExternal = url.startsWith('http');
      return `<a href="${url}" class="text-primary text-decoration-underline" ${isExternal ? 'target="_blank" rel="noopener noreferrer"' : ''}>${linkText}</a>`;
    });
}

